import React, { useState, useEffect, useRef } from 'react';
import { saveAs } from 'file-saver';
import FlexiblePlot from '../charts/FlexiblePlot';

const QueryResults = ({ results }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showPlot, setShowPlot] = useState(false);
  const [selectedRowsForPlot, setSelectedRowsForPlot] = useState([]);
  
  const tableRef = useRef(null);
  
  // Calculate if CSV is too large to display (1MB threshold)
  const isCSVTooLarge = () => {
    if (!results || results.format !== 'csv' || !results.rows) return false;
    
    // Rough estimate: calculate size of JSON stringified data
    const dataSize = JSON.stringify(results.rows).length;
    return dataSize > 1024 * 1024; // 1MB in bytes
  };
  
  // Reset component state when results change entirely
  useEffect(() => {
    // Reset states when results change
    setCurrentPage(1);
    setSortColumn(null);
    setSearchQuery('');
    setSelectedRowsForPlot([]);
    
    // If results become null, reset other states too
    if (!results) {
      setFilteredResults([]);
      setVisibleColumns([]);
      setColumnOrder([]);
      return;
    }
    
    if (results && results.rows) {
      // Initialize column visibility
      if (results.columns && results.columns.length > 0) {
        setVisibleColumns(results.columns);
        setColumnOrder(results.columns);
      }
      
      // Initialize filtered results with all results
      // (the detailed filtering will happen in the second useEffect)
      setFilteredResults([...results.rows]);
    }
  }, [results]);  // Only depend on results changing completely, not on other state changes
  
  // Handle sorting and filtering when those criteria change
  useEffect(() => {
    if (!results || !results.rows) return;
    
    // Filter and sort the results
    let filtered = [...results.rows];
    
    // Apply search filter across all columns
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(row => {
        return Object.values(row).some(value => 
          value !== null && value !== undefined && String(value).toLowerCase().includes(query)
        );
      });
    }
    
    // Apply sorting
    if (sortColumn) {
      filtered = filtered.sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];
        
        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return sortDirection === 'asc' ? -1 : 1;
        if (bValue === null || bValue === undefined) return sortDirection === 'asc' ? 1 : -1;
        
        // Handle different types
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // Default string comparison
        const aString = String(aValue).toLowerCase();
        const bString = String(bValue).toLowerCase();
        
        return sortDirection === 'asc'
          ? aString.localeCompare(bString)
          : bString.localeCompare(aString);
      });
    }
    
    setFilteredResults(filtered);
  }, [results, searchQuery, sortColumn, sortDirection]);
  
  // Calculate pagination
  const totalPages = filteredResults.length
    ? Math.ceil(filteredResults.length / rowsPerPage)
    : 0;
  
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedResults = filteredResults.slice(
    startIndex,
    startIndex + rowsPerPage
  );
  
  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  // Updated download function that checks for results.data first
  const downloadFromResultsLink = () => {
    if (!results || (!results.id && !results.job_id && !results.data)) return;

    // For async queries (with jobId) that have partial data, fetch the full file from the server
    if (results.jobId && results.isPartial) {
      const downloadUrl = `/adss/async/${results.jobId}/results`;

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `query_results_${new Date().toISOString().slice(0, 19)}.${results.format || 'csv'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // If we have raw data (sync queries or complete async data), download directly from it
    if (results.data) {
      let content, mimeType, fileExtension;

      // Determine file type and content
      if (results.format === 'csv' || !results.format) {
        // Convert data to CSV format
        if (results.columns && Array.isArray(results.data)) {
          const header = results.columns.join(',');
          const rows = results.data.map(row =>
            results.columns.map(col => {
              const value = row[col];
              if (value === null || value === undefined) return '';
              const stringValue = String(value);
              // Escape quotes and handle commas
              if (stringValue.includes(',') || stringValue.includes('"')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
              }
              return stringValue;
            }).join(',')
          ).join('\n');
          content = `${header}\n${rows}`;
        } else {
          // Fallback: stringify the data
          content = JSON.stringify(results.data, null, 2);
        }
        mimeType = 'text/csv;charset=utf-8';
        fileExtension = 'csv';
      } else if (results.format === 'json') {
        content = JSON.stringify(results.data, null, 2);
        mimeType = 'application/json';
        fileExtension = 'json';
      } else {
        // For other formats, try to download as-is
        content = typeof results.data === 'string' ? results.data : JSON.stringify(results.data, null, 2);
        mimeType = 'application/octet-stream';
        fileExtension = results.format || 'txt';
      }

      // Create and download blob
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `query_results_${new Date().toISOString().slice(0, 19)}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up
      return;
    }

    // Fallback to API endpoint if no raw data
    const downloadUrl = `/adss/v1/queries/results/${results.id || results.job_id}/download`;

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `query_results_${new Date().toISOString().slice(0, 19)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  
  // Export data functions
  const exportCSV = () => {
    if (!results || !results.rows || !results.columns) return;
    
    // Build CSV content
    const header = visibleColumns.join(',');
    const rows = filteredResults.map(row => 
      visibleColumns.map(col => {
        const value = row[col];
        // Handle values that might need escaping
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // Escape quotes and handle commas
        if (stringValue.includes(',') || stringValue.includes('"')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    ).join('\n');
    
    const csv = `${header}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `query_results_${new Date().toISOString().slice(0, 19)}.csv`);
  };
  
  const exportJSON = () => {
    if (!results || !results.rows) return;
    
    // Extract only visible columns for each row
    const visibleData = filteredResults.map(row => {
      const visibleRow = {};
      visibleColumns.forEach(col => {
        visibleRow[col] = row[col];
      });
      return visibleRow;
    });
    
    const json = JSON.stringify(visibleData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    saveAs(blob, `query_results_${new Date().toISOString().slice(0, 19)}.json`);
  };
  
  // Column management functions
  const toggleColumnVisibility = (column) => {
    if (visibleColumns.includes(column)) {
      setVisibleColumns(visibleColumns.filter(col => col !== column));
    } else {
      // Add back in the original order
      const newColumns = [...visibleColumns];
      const originalIndex = columnOrder.indexOf(column);
      
      // Find where to insert to maintain original order
      let insertIndex = 0;
      while (
        insertIndex < newColumns.length &&
        columnOrder.indexOf(newColumns[insertIndex]) < originalIndex
      ) {
        insertIndex++;
      }
      
      newColumns.splice(insertIndex, 0, column);
      setVisibleColumns(newColumns);
    }
  };
  
  const resetColumns = () => {
    if (results && results.columns) {
      setVisibleColumns(results.columns);
      setColumnOrder(results.columns);
    }
  };
  
  // Toggle plot visibility
  const togglePlot = () => {
    setShowPlot(!showPlot);
  };
  
  // Toggle row selection for plot
  const toggleRowSelection = (row) => {
    if (selectedRowsForPlot.some(r => JSON.stringify(r) === JSON.stringify(row))) {
      setSelectedRowsForPlot(selectedRowsForPlot.filter(r => JSON.stringify(r) !== JSON.stringify(row)));
    } else {
      // Limit to 20 rows for performance
      if (selectedRowsForPlot.length < 20) {
        setSelectedRowsForPlot([...selectedRowsForPlot, row]);
      }
    }
  };
  
  // Check if a row is selected
  const isRowSelected = (row) => {
    return selectedRowsForPlot.some(r => JSON.stringify(r) === JSON.stringify(row));
  };
  
  // Clear all selected rows
  const clearSelectedRows = () => {
    setSelectedRowsForPlot([]);
  };
  
  // If no results or error state, show message
  if (!results || !results.rows || !results.columns || results.error) {
    return (
      <div className="p-6 text-center bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md border border-ui-light-border dark:border-ui-dark-border">
        <h3 className="mb-2 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Query Results</h3>
        {results && results.error ? (
          <div className="p-4 mb-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md border border-status-error-light-text/20 dark:border-status-error-dark-text/20">
            <p className="font-medium">Error parsing query results</p>
            <p className="mt-1">{results.message || 'Failed to parse data from server'}</p>
            <p className="mt-3 text-sm">
              You can still access the raw data by clicking the "Download Raw Results" button in the bottom right corner.
            </p>
          </div>
        ) : (
          <p className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
            No results to display. Execute a query to see results here.
          </p>
        )}
      </div>
    );
  }
  
  // Display the result format and add download option
  const isCSV = results && results.format === 'csv';
  const isParquet = results && results.format === 'parquet';
  const isFITS = results && results.format === 'fits';
  const csvTooLarge = isCSVTooLarge();
  
  // If empty results
  if (results.rows.length === 0) {
    return (
      <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md border border-ui-light-border dark:border-ui-dark-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Query Results</h3>
          <span className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
            Query returned no rows
          </span>
        </div>
        <div className="p-4 text-center border border-ui-light-border dark:border-ui-dark-border rounded-md">
          <p className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
            The query completed successfully but returned no results.
          </p>
        </div>
      </div>
    );
  }
  
  // If it's a Parquet file, FITS file, or CSV too large, just show download button
  if (isParquet || results.isParquetDownload || isFITS || results.isFITSDownload || csvTooLarge) {
    // Create a download function for the file data
    const handleDownload = () => {
      console.log('Downloading results:', results);

      // For async queries (with jobId), always fetch the full file from the server
      if (results.jobId) {
        const downloadUrl = `/adss/async/${results.jobId}/results`;

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `query_results_${new Date().toISOString().slice(0, 19)}.${isFITS ? 'fits' : isCSV ? 'csv' : 'parquet'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      // If we have raw data and it's NOT a partial async result, download directly from it
      if (results.data && !results.isPartial) {
        let content, mimeType, fileExtension;

        if (isFITS) {
          // For FITS files, the data should be binary
          content = results.data;
          mimeType = 'application/octet-stream';
          fileExtension = 'fits';
        } else if (isParquet || results.isParquetDownload) {
          // For Parquet files, the data should be binary
          content = results.data;
          mimeType = 'application/octet-stream';
          fileExtension = 'parquet';
        } else if (csvTooLarge) {
          // For large CSV files
          if (results.columns && Array.isArray(results.data)) {
            const header = results.columns.join(',');
            const rows = results.data.map(row =>
              results.columns.map(col => {
                const value = row[col];
                if (value === null || value === undefined) return '';
                const stringValue = String(value);
                if (stringValue.includes(',') || stringValue.includes('"')) {
                  return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
              }).join(',')
            ).join('\n');
            content = `${header}\n${rows}`;
          } else {
            content = typeof results.data === 'string' ? results.data : JSON.stringify(results.data, null, 2);
          }
          mimeType = 'text/csv;charset=utf-8';
          fileExtension = 'csv';
        }

        // Create and download blob
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `query_results_${new Date().toISOString().slice(0, 19)}.${fileExtension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url); // Clean up
        return;
      }

      // Fallback to API endpoint
      const downloadUrl = `/adss/v1/queries/results/${results.id || results.job_id}/download`;

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `query_results_${new Date().toISOString().slice(0, 19)}.${isFITS ? 'fits' : isCSV ? 'csv' : 'parquet'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    // Different formatting for different file types
    let formatLabel, formatClass, formatDescription;
    
    if (csvTooLarge) {
      formatLabel = 'CSV (Large)';
      formatClass = 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
      formatDescription = 'This CSV file is too large to display in the browser. Use the button below to download the file.';
    } else if (isFITS) {
      formatLabel = 'FITS';
      formatClass = 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400';
      formatDescription = 'FITS (Flexible Image Transport System) files need to be downloaded for viewing. Use the button below to download the result file.';
    } else {
      formatLabel = 'PARQUET';
      formatClass = 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400';
      formatDescription = 'Parquet files need to be downloaded for viewing. Use the button below to download the result file.';
    }

    return (
      <div className="p-6 text-center bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md border border-ui-light-border dark:border-ui-dark-border">
        <div className="flex items-center justify-center mb-4">
          <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mr-3">
            Query Results
          </h3>
          <span className={`px-2 py-1 text-xs rounded-full ${formatClass}`}>
            {formatLabel}
          </span>
        </div>
        
        <p className="mb-4 text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
          {formatDescription}
        </p>
        
        <button
          onClick={handleDownload}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
        >
          Download {formatLabel.includes('Large') ? 'CSV' : formatLabel} File
        </button>
      </div>
    );
  }
  
  // Regular display for CSV and other formats
  return (
    <div className="p-4 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md border border-ui-light-border dark:border-ui-dark-border">      
      <div className="flex flex-wrap items-center justify-between mb-4">
        <div className="flex items-center">
          <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
            Query Results
          </h3>
          {results.format && (
            <span className={`ml-3 px-2 py-1 text-xs rounded-full ${
              isCSV 
                ? 'bg-status-success-light-bg dark:bg-status-success-dark-bg text-status-success-light-text dark:text-status-success-dark-text' 
                : 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
            }`}>
              {results.format.toUpperCase()}
            </span>
          )}
          
          {/* Toggle Plot Button */}
          <button
            onClick={togglePlot}
            className="ml-4 px-3 py-1 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            {showPlot ? 'Hide Plot' : 'Show Plot'}
          </button>
          
          {/* Row selection info */}
          {selectedRowsForPlot.length > 0 && (
            <div className="ml-3 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
              <span>{selectedRowsForPlot.length} rows selected</span>
              <button 
                onClick={clearSelectedRows}
                className="ml-2 text-xs text-status-error-light-text dark:text-status-error-dark-text hover:text-status-error-light-text/80 dark:hover:text-status-error-dark-text/80 transition-colors"
              >
                Clear
              </button>
            </div>
          )}
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
            Showing {filteredResults.length.toLocaleString()} rows
          </span>
          
          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="px-3 py-1 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
            >
              Columns
            </button>
            
            {showColumnSelector && (
              <div className="absolute right-0 z-10 w-56 mt-2 overflow-auto bg-ui-light-card dark:bg-ui-dark-card rounded-md shadow-lg max-h-60 border border-ui-light-border dark:border-ui-dark-border">
                <div className="p-2 border-b border-ui-light-border dark:border-ui-dark-border">
                  <button
                    onClick={resetColumns}
                    className="w-full px-2 py-1 text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                  >
                    Reset Columns
                  </button>
                </div>
                <div className="p-2">
                  {columnOrder.map(column => (
                    <div key={column} className="flex items-center py-1">
                      <input
                        type="checkbox"
                        id={`col-${column}`}
                        checked={visibleColumns.includes(column)}
                        onChange={() => toggleColumnVisibility(column)}
                        className="w-4 h-4 text-primary-600 dark:text-primary-400 border-ui-light-border dark:border-ui-dark-border rounded focus:ring-primary-500 dark:focus:ring-primary-400"
                      />
                      <label
                        htmlFor={`col-${column}`}
                        className="ml-2 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary truncate"
                        title={column}
                      >
                        {column}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={exportCSV}
              className="px-3 py-1 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
            >
              CSV
            </button>
            <button
              onClick={exportJSON}
              className="px-3 py-1 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
            >
              JSON
            </button>
          </div>
        </div>
      </div>
      
      <div className="flex items-center mb-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search results..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
          />
        </div>
        <div className="flex items-center ml-4">
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1); // Reset to first page when changing rows per page
            }}
            className="px-3 py-2 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-ui-dark-background transition-colors"
          >
            <option value={10}>10 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
            <option value={500}>500 rows</option>
            <option value={1000}>1000 rows</option>
          </select>
        </div>
      </div>
      
      {/* Plot Component */}
      {showPlot && (
        <div className="mb-6">
          {selectedRowsForPlot.length > 0 ? (
            <FlexiblePlot 
              objectData={selectedRowsForPlot.length === 1 ? selectedRowsForPlot[0] : selectedRowsForPlot[0]} 
              metadata={{ important_columns: visibleColumns.filter(col => 
                typeof selectedRowsForPlot[0][col] === 'number' && !isNaN(selectedRowsForPlot[0][col])
              )}}
              multipleObjects={selectedRowsForPlot.length > 1 ? selectedRowsForPlot : null}
            />
          ) : (
            <div className="p-6 text-center bg-ui-light-card dark:bg-ui-dark-card shadow-md rounded-lg border border-ui-light-border dark:border-ui-dark-border">
              <p className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Select rows from the table below to plot their data</p>
              <p className="text-sm text-ui-light-text-muted dark:text-ui-dark-text-muted mt-2">Click on any row to select it for plotting</p>
            </div>
          )}
        </div>
      )}
      
      <div className="overflow-x-auto border border-ui-light-border dark:border-ui-dark-border rounded-md">
        <table ref={tableRef} className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
          <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
            <tr>
              {/* Selection column */}
              <th className="w-10 px-2 py-3 text-center text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                Plot
              </th>
              
              {visibleColumns.map((column) => (
                <th
                  key={column}
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider cursor-pointer hover:bg-ui-light-card dark:hover:bg-ui-dark-card transition-colors"
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center">
                    <span>{column}</span>
                    {sortColumn === column && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
            {paginatedResults.map((row, rowIndex) => (
              <tr 
                key={rowIndex} 
                className={`hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors ${isRowSelected(row) ? 'bg-primary-50 dark:bg-primary-900/30' : ''}`}
                onClick={() => toggleRowSelection(row)}
              >
                {/* Selection checkbox */}
                <td className="w-10 px-2 py-4 whitespace-nowrap text-center">
                  <input 
                    type="checkbox" 
                    checked={isRowSelected(row)}
                    onChange={() => {}} // Controlled by row click
                    className="h-4 w-4 text-primary-600 dark:text-primary-400 border-ui-light-border dark:border-ui-dark-border rounded focus:ring-primary-500 dark:focus:ring-primary-400"
                  />
                </td>
                
                {visibleColumns.map((column, colIndex) => {
                  const cellValue = row[column];
                  const displayValue = cellValue === null || cellValue === undefined
                    ? ''
                    : String(cellValue);
                    
                  // Determine if this is a numeric column for right alignment
                  const isNumeric = typeof cellValue === 'number';
                  
                  return (
                    <td
                      key={`${rowIndex}-${colIndex}`}
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        isNumeric 
                          ? 'text-right text-ui-light-text-secondary dark:text-ui-dark-text-secondary' 
                          : 'text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary'
                      }`}
                    >
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 mt-4 bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md sm:px-6">
          <div className="flex justify-between flex-1 sm:hidden">
            <button
              onClick={() => setCurrentPage(curr => Math.max(curr - 1, 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 transition-colors"
            >
              Previous
            </button>
            <span className="relative z-0 inline-flex shadow-sm">
              <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border">
                Page {currentPage} of {totalPages}
              </span>
            </span>
            <button
              onClick={() => setCurrentPage(curr => Math.min(curr + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 transition-colors"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                Showing{" "}
                <span className="font-medium">{(currentPage - 1) * rowsPerPage + 1}</span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * rowsPerPage, filteredResults.length)}
                </span>{" "}
                of <span className="font-medium">{filteredResults.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-l-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 transition-colors"
                >
                  <span className="sr-only">First</span>
                  <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentPage(curr => Math.max(curr - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 transition-colors"
                >
                  <span className="sr-only">Previous</span>
                  <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                {/* Page numbers */}
                {[...Array(Math.min(5, totalPages))].map((_, index) => {
                  let pageNum;
                  
                  // Calculate which page numbers to show
                  if (totalPages <= 5) {
                    pageNum = index + 1;
                  } else if (currentPage <= 3) {
                    pageNum = index + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + index;
                  } else {
                    pageNum = currentPage - 2 + index;
                  }
                  
                  if (pageNum <= totalPages) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-primary-50 dark:bg-primary-900/30 border-primary-500 dark:border-primary-700 text-primary-600 dark:text-primary-400'
                            : 'bg-ui-light-card dark:bg-ui-dark-card border-ui-light-border dark:border-ui-dark-border text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover'
                        } border transition-colors`}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                  return null;
                })}
                
                <button
                  onClick={() => setCurrentPage(curr => Math.min(curr + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 transition-colors"
                >
                  <span className="sr-only">Next</span>
                  <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-r-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 transition-colors"
                >
                  <span className="sr-only">Last</span>
                  <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryResults;